import { getPageUrl, getMetaData, getJsFromString } from '@/utils';
import { alwaysNotVisibleBlocks, honeyBotId } from '@/constants';
import { useConfigStore, useFormProps } from '../stores/ConfigStore';
import { useBlockStore } from '../stores/BlockStore';
import { useFormStore } from '../stores/FormStore';
import useFormCompletionPercentage from './useFormCompletionPercentage';

export default function useFeedback({
	addExtraData,
	noOpenOrPIIFeedback,
	excludeScreenShotDom,
	addHoney
} = {}) {
	const blockState = useBlockStore();
	const { webView, triggerMethod } = useFormProps();
	const { blocks, properties, translationSet, layout } = useConfigStore();
	const { sendOptions } = useConfigStore();
	const { feedbackId, customerId } = useFormStore();
	const formCompletionPercentage = useFormCompletionPercentage();

	const feedbackFromSendOptions = sendOptions.data.reduce((allFeedback, sendOption) => {
		const { field, type, label } = sendOption;

		const [blockId, extra1, extra2] = field
			.replace(/([^1-9]+)/, '') //remove everything before first number
			.split('_');

		const block = blocks[blockId];
		const state = blockState[blockId];
		const value = getValue({
			block,
			state,
			extra1,
			extra2,
			type
		});

		const feedback = {
			label: label,
			value,
			type: webView ? type.replace('image', 'mobile_image') : type,
			...(addExtraData && {
				title: block.title,
				id: block.id,
				blockType: block.typeName,
				field: field
			})
		};

		if (noOpenOrPIIFeedback) {
			if (
				['input', 'textarea', 'screenshot', 'website_data'].indexOf(block.typeName) >
					-1 ||
				[
					'customer',
					'id',
					'url',
					'title',
					'firstname',
					'lastname',
					'phone',
					'email'
				].indexOf(type) > -1 ||
				label === 'Page title'
			) {
				return allFeedback;
			}
		}

		if (excludeScreenShotDom) {
			if (['screenshot'].indexOf(block.typeName) > -1 && ['dom'].indexOf(type) > -1) {
				return allFeedback;
			}
		}

		allFeedback.push(feedback);

		return allFeedback;
	}, []);

	const metaData = getMetaData(webView);

	return [
		...feedbackFromSendOptions,
		{
			value: window.navigator.userAgent,
			type: 'agent',
			label: 'User Agent'
		},
		{
			value: getPageUrl(),
			type: 'url',
			label: 'url'
		},
		{
			value: document.title,
			type: 'category',
			label: 'Page title'
		},
		{
			value: 1,
			type: 'role',
			label: 'Role'
		},
		{
			value: properties.name,
			type: 'category',
			label: 'Survey'
		},
		{
			value: `${window.innerWidth}x${window.innerHeight}`,
			type: 'viewport',
			label: 'Viewport'
		},
		{
			value: triggerMethod,
			type: 'category',
			label: 'Form trigger'
		},
		{
			value: formCompletionPercentage,
			type: 'form_completion',
			label: 'Form completion percentage'
		},
		{
			value: feedbackId,
			type: 'id',
			label: 'Survey ID'
		},
		...(translationSet
			? [
					{
						value: translationSet,
						type: 'category',
						label: 'Translation'
					}
			  ]
			: []),
		...metaData,
		...(properties.advanced.add_contentsquare
			? [
					{
						value: getJsFromString('CS_CONF.integrations_handler.mopinion'),
						type: 'category',
						label: 'Contentsquare session recording'
					}
			  ]
			: []),
		...(properties.conversational
			? [
					{
						value: getEndingElementTitle(
							layout
								.filter(
									blockId =>
										blockState[blockId]?.isVisible &&
										blockState[blockId]?.showInConvo &&
										alwaysNotVisibleBlocks.indexOf(blocks[blockId]?.typeName) === -1
								)
								.map(blockId => blocks[blockId])
								.pop()
						),
						type: 'category',
						label: 'Ending element'
					}
			  ]
			: []),
		...(sendOptions.force_customer !== false
			? [{ value: customerId, type: 'customer', label: sendOptions.force_customer.label }]
			: []),
		...(addHoney && blockState[honeyBotId]?.value
			? [{ value: blockState[honeyBotId].value, type: 'honeybot', label: 'honeybot' }]
			: [])
	];
}

function getValue({ block = {}, state = {}, extra1, extra2, type }) {
	if (block.typeName === 'screenshot' && state[type]) {
		return state[type]?.value;
	}

	if (block.typeName === 'matrix') {
		return state[block.properties?.elements?.[extra1]?.data_field?.[extra2]]?.value;
	}

	//a normal field
	if (!extra1 && !extra2) {
		return state.value;
	}

	if (extra1 === 'extra' && extra2) {
		return state[block.properties?.elements_extra?.[extra2]?.data_field]?.value;
	}
	if (extra1 === 'name' && !block.properties?.elements?.name?.combine) {
		return state[block.properties?.elements?.name?.subelements?.lastname?.data_field]
			?.value;
	}
	if (extra1 === 'firstname') {
		return state[block.properties?.elements?.name?.subelements?.firstname?.data_field]
			?.value;
	}

	return state[block.properties?.elements?.[extra1].data_field]?.value;
}

function getEndingElementTitle(block = {}) {
	if (!block?.typeName) {
		return '';
	}

	switch (block?.typeName) {
		case 'ending':
		case 'link':
			return block.properties.description;
		default:
			return block.title;
	}
}
